/**
 * FeedbackPanel — Slide-out panel for submitting team feedback.
 *
 * Entry point: feedback button in Layout header (gated by module.feedback flag).
 * Three submission types: Bug Report / Feature Request / Automation Request.
 * Voice input lives in Phase 5 — the recorder button in the type selector is
 * wired to a no-op in this build and will be activated when VoiceRecorder.jsx
 * ships.
 *
 * Flow:
 *   1. User opens the panel → type selector.
 *   2. Picks a type → type-specific form.
 *   3. Fills + submits → success toast, panel closes.
 *   4. "View my submissions" link navigates to /feedback.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VoiceRecorder from "./VoiceRecorder";

const TYPES = [
  {
    key: "bug_report",
    label: "Bug Report",
    emoji: "🐛",
    tagline: "Something is broken",
  },
  {
    key: "feature_request",
    label: "Feature Request",
    emoji: "✨",
    tagline: "Could work better",
  },
  {
    key: "automation_request",
    label: "Automation",
    emoji: "⚡",
    tagline: "I do this repeatedly",
  },
];

const SEVERITY_OPTS = [
  { value: "blocker", label: "Blocker — system unusable" },
  { value: "major", label: "Major — significant impact" },
  { value: "minor", label: "Minor — cosmetic or small" },
  { value: "trivial", label: "Trivial — typo, alignment" },
];

const FREQUENCY_OPTS = [
  { value: "every_time", label: "Every time" },
  { value: "sometimes", label: "Sometimes" },
  { value: "once", label: "Once" },
  { value: "unknown", label: "Unknown" },
];

const AUTOMATION_FREQ_OPTS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "ad_hoc", label: "Ad hoc" },
  { value: "other", label: "Other" },
];

const DEFAULT_MODULES = [
  "timesheets", "screening", "crm", "intelligence", "coordination",
  "reports", "ai_hub", "settings", "other",
];

export default function FeedbackPanel({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("select"); // select | transcribing | form | success
  const [type, setType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [error, setError] = useState(null);
  const [voiceTranscript, setVoiceTranscript] = useState(null);
  const [voiceConfidence, setVoiceConfidence] = useState(null);

  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    fetch("/api/platform-control/settings/feedback.modules_list")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.value) return;
        try {
          const parsed = typeof data.value === "string" ? JSON.parse(data.value) : data.value;
          if (Array.isArray(parsed) && parsed.length > 0) setModules(parsed);
        } catch {}
      })
      .catch(() => {});
  }, [open]);

  // Reset state when panel re-opens
  useEffect(() => {
    if (open) {
      setStep("select");
      setType(null);
      setForm({});
      setSubmittedId(null);
      setError(null);
      setVoiceTranscript(null);
      setVoiceConfidence(null);
    }
  }, [open]);

  // ── Voice flow ──────────────────────────────────────────────────
  const handleVoiceStop = async (blob) => {
    setStep("transcribing");
    setError(null);
    try {
      const body = new FormData();
      body.append("audio", blob, "feedback.webm");
      body.append("parse", "true");
      const res = await fetch("/api/feedback/voice/transcribe", { method: "POST", body });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || `Transcription failed (${res.status})`);
      }
      const data = await res.json();
      setVoiceTranscript(data.transcript || "");

      if (data.parsed) {
        // Pre-fill form from parsed structured data
        const p = data.parsed;
        setType(p.feedback_type);
        setVoiceConfidence(p.confidence);
        const nextForm = {
          title: p.title || "",
          description: p.description || data.transcript || "",
        };
        const td = p.type_details || {};
        if (p.feedback_type === "bug_report") {
          Object.assign(nextForm, {
            severity: td.severity || "",
            steps_to_reproduce: td.steps_to_reproduce || "",
            expected_behavior: td.expected_behavior || "",
            actual_behavior: td.actual_behavior || "",
            frequency: td.frequency || "",
            ros_module: td.ros_module || "",
            workaround: td.workaround || "",
          });
        } else if (p.feedback_type === "feature_request") {
          Object.assign(nextForm, {
            current_behavior: td.current_behavior || "",
            desired_behavior: td.desired_behavior || "",
            business_justification: td.business_justification || "",
            ros_module: td.ros_module || "",
            affects_others: !!td.affects_others,
          });
        } else if (p.feedback_type === "automation_request") {
          Object.assign(nextForm, {
            process_description: td.process_description || "",
            frequency: td.frequency || "",
            time_per_occurrence_minutes: td.time_per_occurrence_minutes || "",
            tools_involved: td.tools_involved || "",
            pain_points: td.pain_points || "",
            ros_module: td.ros_module || "",
          });
        }
        setForm(nextForm);
        setStep("form");
      } else {
        // Parse failed — let user pick type, pre-fill description only
        setForm({ description: data.transcript || "" });
        setStep("select");
        setError("Couldn't auto-detect type from transcript — pick one and review the pre-filled description.");
      }
    } catch (err) {
      setError(err.message || String(err));
      setStep("select");
    }
  };

  const handleVoiceError = (msg) => {
    setError(msg);
  };

  const handlePickType = (typeKey) => {
    setType(typeKey);
    setForm({});
    setStep("form");
  };

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const buildTypeDetails = () => {
    if (type === "bug_report") {
      return {
        severity: form.severity || null,
        steps_to_reproduce: form.steps_to_reproduce || "",
        expected_behavior: form.expected_behavior || "",
        actual_behavior: form.actual_behavior || "",
        frequency: form.frequency || null,
        ros_module: form.ros_module || null,
        workaround: form.workaround || "",
      };
    }
    if (type === "feature_request") {
      return {
        current_behavior: form.current_behavior || "",
        desired_behavior: form.desired_behavior || "",
        business_justification: form.business_justification || "",
        ros_module: form.ros_module || null,
        affects_others: !!form.affects_others,
      };
    }
    if (type === "automation_request") {
      return {
        process_description: form.process_description || "",
        frequency: form.frequency || null,
        time_per_occurrence_minutes: form.time_per_occurrence_minutes
          ? Number(form.time_per_occurrence_minutes) : null,
        tools_involved: form.tools_involved || "",
        pain_points: form.pain_points || "",
        ros_module: form.ros_module || null,
      };
    }
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const payload = {
      feedback_type: type,
      title: (form.title || "").trim(),
      description: (form.description || "").trim(),
      type_details: buildTypeDetails(),
      attachments: form.attachments || [],
      ros_page_url: window.location.href,
      browser_info: navigator.userAgent?.slice(0, 500) || null,
      voice_used: !!voiceTranscript,
      voice_transcript: voiceTranscript || null,
    };

    if (!payload.title) { setError("Title is required"); setSubmitting(false); return; }
    if (!payload.description) { setError("Description is required"); setSubmitting(false); return; }

    try {
      const res = await fetch("/api/feedback/submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || `Submission failed (${res.status})`);
      }
      const created = await res.json();
      setSubmittedId(created.id);
      setStep("success");
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnother = () => {
    setStep("select");
    setType(null);
    setForm({});
    setSubmittedId(null);
    setError(null);
  };

  const handleViewMine = () => {
    onClose();
    navigate("/feedback");
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed right-0 top-0 bottom-0 z-[61] w-full max-w-[500px] bg-white dark:bg-brand-navy shadow-2xl border-l border-neutral-200 dark:border-white/10 flex flex-col"
        role="dialog"
        aria-label="Submit feedback"
      >
        <div className="h-14 flex items-center justify-between px-4 border-b border-neutral-200 dark:border-white/10">
          <div className="flex items-center gap-2">
            <span className="text-xl">💬</span>
            <h2 className="font-semibold text-neutral-800 dark:text-white">
              {step === "success" ? "Submitted" : "Submit Feedback"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 dark:hover:bg-white/10"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {step === "select" && (
            <TypeSelector
              onPick={handlePickType}
              onVoiceStop={handleVoiceStop}
              onVoiceError={handleVoiceError}
              error={error}
            />
          )}
          {step === "transcribing" && <TranscribingView />}
          {step === "form" && (
            <FormView
              type={type}
              form={form}
              updateField={updateField}
              modules={modules}
              voiceTranscript={voiceTranscript}
              voiceConfidence={voiceConfidence}
              onBack={() => setStep("select")}
              onSubmit={handleSubmit}
              submitting={submitting}
              error={error}
            />
          )}
          {step === "success" && (
            <SuccessView
              submittedId={submittedId}
              onSubmitAnother={handleSubmitAnother}
              onViewMine={handleViewMine}
            />
          )}
        </div>
      </aside>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────

function TypeSelector({ onPick, onVoiceStop, onVoiceError, error }) {
  return (
    <div className="p-5 space-y-4">
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        What would you like to submit?
      </p>
      <div className="grid grid-cols-3 gap-2">
        {TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => onPick(t.key)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-neutral-200 dark:border-white/10 hover:border-brand-blue hover:bg-brand-blue/5 dark:hover:bg-brand-blue/10 transition-colors text-center"
          >
            <span className="text-3xl">{t.emoji}</span>
            <span className="text-sm font-medium text-neutral-800 dark:text-white">{t.label}</span>
            <span className="text-[11px] text-neutral-500 leading-tight">{t.tagline}</span>
          </button>
        ))}
      </div>

      <div className="relative py-1">
        <div className="absolute inset-0 flex items-center" aria-hidden>
          <div className="w-full border-t border-neutral-200 dark:border-white/10" />
        </div>
        <div className="relative flex justify-center">
          <span className="px-2 bg-white dark:bg-brand-navy text-[11px] text-neutral-400 uppercase tracking-wider">or</span>
        </div>
      </div>

      <VoiceRecorder onStop={onVoiceStop} onError={onVoiceError} />
      <p className="text-[11px] text-center text-neutral-400 leading-relaxed">
        Describe your issue. We'll transcribe, detect the type, and pre-fill the form for you.
      </p>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 px-3 py-2 rounded">
          {error}
        </div>
      )}
    </div>
  );
}

function TranscribingView() {
  return (
    <div className="p-8 flex flex-col items-center justify-center gap-4 text-center">
      <div className="w-10 h-10 rounded-full border-4 border-neutral-200 border-t-brand-blue animate-spin" />
      <div>
        <div className="font-medium text-neutral-800 dark:text-white">Transcribing...</div>
        <div className="text-xs text-neutral-500 mt-1">Sending to Whisper then parsing the transcript.</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────

function FormView({ type, form, updateField, modules, voiceTranscript, voiceConfidence, onBack, onSubmit, submitting, error }) {
  const typeInfo = TYPES.find((t) => t.key === type);

  return (
    <form onSubmit={onSubmit} className="p-5 space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-brand-blue hover:underline"
      >
        ← Change type
      </button>

      <div className="flex items-center gap-2">
        <span className="text-2xl">{typeInfo?.emoji}</span>
        <h3 className="font-semibold text-neutral-800 dark:text-white">{typeInfo?.label}</h3>
      </div>

      {voiceTranscript && (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 text-xs">
          <div className="flex items-center gap-1.5 font-medium text-indigo-800 dark:text-indigo-300 mb-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0m7 7v3m0 0h3m-3 0H9m3-3a3 3 0 01-3-3V6a3 3 0 016 0v8a3 3 0 01-3 3z" />
            </svg>
            Voice transcript
            {typeof voiceConfidence === "number" && (
              <span className="ml-auto text-[10px] text-indigo-600 dark:text-indigo-400">
                {Math.round(voiceConfidence * 100)}% confident
              </span>
            )}
          </div>
          <p className="text-indigo-900 dark:text-indigo-100 italic line-clamp-4">"{voiceTranscript}"</p>
          <p className="mt-1 text-[10px] text-indigo-700 dark:text-indigo-400">
            Review and edit the auto-filled fields below before submitting.
          </p>
        </div>
      )}

      {/* Shared fields */}
      <Field label="Title" required>
        <input
          type="text"
          value={form.title || ""}
          onChange={(e) => updateField("title", e.target.value)}
          maxLength={255}
          required
          className={inputCls}
          placeholder="Short summary"
        />
      </Field>

      <Field label="Description" required>
        <textarea
          value={form.description || ""}
          onChange={(e) => updateField("description", e.target.value)}
          required
          rows={4}
          className={inputCls}
          placeholder="What's going on?"
        />
      </Field>

      <Field label="ROS Module">
        <select
          value={form.ros_module || ""}
          onChange={(e) => updateField("ros_module", e.target.value || null)}
          className={inputCls}
        >
          <option value="">— select —</option>
          {modules.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </Field>

      {/* Type-specific */}
      {type === "bug_report" && (
        <>
          <Field label="Severity">
            <select
              value={form.severity || ""}
              onChange={(e) => updateField("severity", e.target.value || null)}
              className={inputCls}
            >
              <option value="">— select —</option>
              {SEVERITY_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Steps to Reproduce">
            <textarea
              value={form.steps_to_reproduce || ""}
              onChange={(e) => updateField("steps_to_reproduce", e.target.value)}
              rows={3}
              className={inputCls}
              placeholder="1. ... 2. ... 3. ..."
            />
          </Field>
          <Field label="Expected Behavior">
            <textarea
              value={form.expected_behavior || ""}
              onChange={(e) => updateField("expected_behavior", e.target.value)}
              rows={2}
              className={inputCls}
            />
          </Field>
          <Field label="Actual Behavior">
            <textarea
              value={form.actual_behavior || ""}
              onChange={(e) => updateField("actual_behavior", e.target.value)}
              rows={2}
              className={inputCls}
            />
          </Field>
          <Field label="Frequency">
            <select
              value={form.frequency || ""}
              onChange={(e) => updateField("frequency", e.target.value || null)}
              className={inputCls}
            >
              <option value="">— select —</option>
              {FREQUENCY_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Workaround">
            <textarea
              value={form.workaround || ""}
              onChange={(e) => updateField("workaround", e.target.value)}
              rows={2}
              className={inputCls}
            />
          </Field>
        </>
      )}

      {type === "feature_request" && (
        <>
          <Field label="Current Behavior">
            <textarea
              value={form.current_behavior || ""}
              onChange={(e) => updateField("current_behavior", e.target.value)}
              rows={3}
              className={inputCls}
              placeholder="How does it work today?"
            />
          </Field>
          <Field label="Desired Behavior">
            <textarea
              value={form.desired_behavior || ""}
              onChange={(e) => updateField("desired_behavior", e.target.value)}
              rows={3}
              className={inputCls}
              placeholder="How would you like it to work?"
            />
          </Field>
          <Field label="Business Justification">
            <textarea
              value={form.business_justification || ""}
              onChange={(e) => updateField("business_justification", e.target.value)}
              rows={2}
              className={inputCls}
              placeholder="Why does this matter?"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
            <input
              type="checkbox"
              checked={!!form.affects_others}
              onChange={(e) => updateField("affects_others", e.target.checked)}
              className="rounded border-neutral-300"
            />
            This would help others on the team too
          </label>
        </>
      )}

      {type === "automation_request" && (
        <>
          <Field label="Process Description" required>
            <textarea
              value={form.process_description || ""}
              onChange={(e) => updateField("process_description", e.target.value)}
              rows={4}
              className={inputCls}
              placeholder="Step-by-step — what do you do?"
              required
            />
          </Field>
          <Field label="How Often" required>
            <select
              value={form.frequency || ""}
              onChange={(e) => updateField("frequency", e.target.value || null)}
              className={inputCls}
              required
            >
              <option value="">— select —</option>
              {AUTOMATION_FREQ_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Time Per Occurrence (minutes)">
            <input
              type="number"
              min={1}
              value={form.time_per_occurrence_minutes || ""}
              onChange={(e) => updateField("time_per_occurrence_minutes", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Tools Involved">
            <input
              type="text"
              value={form.tools_involved || ""}
              onChange={(e) => updateField("tools_involved", e.target.value)}
              className={inputCls}
              placeholder="ROS, Excel, Outlook ..."
            />
          </Field>
          <Field label="Pain Points">
            <textarea
              value={form.pain_points || ""}
              onChange={(e) => updateField("pain_points", e.target.value)}
              rows={2}
              className={inputCls}
              placeholder="What's annoying about the current process?"
            />
          </Field>
        </>
      )}

      {error && (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 px-3 py-2 rounded">
          {error}
        </div>
      )}

      <div className="pt-2 flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-blue/90 disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
        {label}{required && <span className="text-red-500"> *</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800 text-sm text-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent";

// ──────────────────────────────────────────────────────────────────────

function SuccessView({ submittedId, onSubmitAnother, onViewMine }) {
  return (
    <div className="p-8 space-y-4 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
        <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="font-semibold text-lg text-neutral-800 dark:text-white">Thanks — submitted</h3>
      <p className="text-xs text-neutral-500 font-mono">{submittedId}</p>
      <div className="flex gap-2 justify-center pt-2">
        <button
          onClick={onSubmitAnother}
          className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-white/20 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5"
        >
          Submit another
        </button>
        <button
          onClick={onViewMine}
          className="px-4 py-2 rounded-lg bg-brand-blue text-white text-sm font-medium hover:bg-brand-blue/90"
        >
          View my submissions
        </button>
      </div>
    </div>
  );
}
